import { BreachPathAppView } from '../view/BreachPathAppView';
import { templateBreachPathApp } from '../view/template';
import { BreachPathAppRepository } from '../repository/BreachPathAppRepository';
import { BaseWebComponentController } from '../../../framework/BaseWebComponentController';
import { BreachPathAppWebComponent } from '..';
import {
  getSideNavigationPanelSmartViewsListener,
  getSmartViewsListeners,
} from 'apps/webcomponents/src/grid/genericGridSmartViews/utils';
import { BreachPathEvents } from '../../common/types';
import { GridEvents } from 'apps/webcomponents/src/grid/types/common';
import { BreachPathIssueRowData, BreachPathAppWebComponentSchema } from '../types'
import { BreachPathSideMenuWebComponent } from '../../breachPathSideMenu/component/BreachPathSideMenuWebComponent';
import { NavigationHelper } from '@nirvana/shared/navigation';
import { SideMenuEvents } from '../../../ui/gzSideMenu/types';
import { GridElement } from '@vaadin/vaadin-grid/vaadin-grid';
import { ActionsEvents } from 'apps/webcomponents/src/riskManagement/common/constants';
import { updateWatchlist } from 'apps/webcomponents/src/riskManagement/common/utils';

export class BreachPathAppController extends BaseWebComponentController {
  public component: BreachPathAppWebComponent;
  public dataRepository: BreachPathAppRepository;
  public view: BreachPathAppView;
  public viewConstructor: typeof BreachPathAppView;
  public dataRepositoryConstructor: typeof BreachPathAppRepository;

  constructor(component: BreachPathAppWebComponent) {
    super(component);

    this.initialSetup({
      viewConstructor: BreachPathAppView,
      viewTemplate: templateBreachPathApp,
      dataRepositoryConstructor: BreachPathAppRepository,
    });
  }

  async render() {
    // inject base web component properties to view
    this.view.breachPathIssueId = this.component.breachPathIssueId;

    await super.render();

    // show/hide sidemenu based on breachPathIssueId in url
    if (this.view.breachPathIssueId) {
      this.showSideMenu();
    } else {
      this.closeSideMenu();
    }
  }

  computeEventListeners() {
    return [
      ...getSmartViewsListeners(
        this.view.dom.$grid,
        this.view.dom.$gridSmartViews,
        this.view.buildGrid.bind(this.view),
        this.view.getTranslations(),
      ),
      ...getSideNavigationPanelSmartViewsListener(this.view.dom.$pushBodyLayout, this.view.dom.$gridSmartViews),
      {
        element: this.view.dom.$grid,
        event: GridEvents.ROW_CLICKED,
        callback: this.handleRowClicked,
      },
      {
        element: this.view.dom.$sideMenu,
        event: ActionsEvents.WATCHLIST_CHANGED,
        callback: this.handleWatchlistChangedEvent,
      },
      {
        element: this.view.dom.$grid,
        event: ActionsEvents.WATCHLIST_CHANGED,
        callback: this.handleWatchlistChangedEvent,
      },
      {
        element: this.view.dom.$sideMenu,
        event: BreachPathEvents.VIEW_DETAILS,
        callback: this.handleViewDetails,
      },
      {
        element: this.view.dom.$sideMenu,
        event: BreachPathEvents.VIEW_RESOURCES,
        callback: this.handleViewResources,
      },
      // {
      //   element: this.view.dom.$feedbackFormButton,
      //   event: BreachPathEvents.OPEN_FEEDBACK_FORM,
      //   callback: this.handleOpenFeedbackFormEvent,
      // },
      {
        element: this.view.dom.$sideMenu,
        event: SideMenuEvents.CLOSE,
        callback: this.handleSideMenuClose,
      },
      {
        element: this.view.dom.$grid,
        event: GridEvents.GRID_LOADED,
        callback: this.highlightGridRow,
      },
    ];
  }

  async getDataForView(): Promise<BreachPathAppWebComponentSchema> {
    return {
      data: await this.dataRepository.getDataForView(),
      externalFilters: [],
    };
  }

  async handleRowClicked(event: CustomEvent<{ rowDetails: BreachPathIssueRowData }>) {
    if (!event.detail.rowDetails) {
      // header was clicked, so we do nothing
      return;
    }

    this.showSideMenu(event.detail.rowDetails);
    NavigationHelper.navigateTo(`/risk-management-breach-path/${event.detail.rowDetails._id}`);
  }

  private async handleWatchlistChangedEvent(event: CustomEvent) {
    const { newWatchlist, ids, companyId } = event.detail;
    await this.updateWatchlistStatus(newWatchlist, ids, companyId);
  }

  private async updateWatchlistStatus(newWatchlistStatus: number, indicatorsIds: Array<number>, companyId: string) {
    await updateWatchlist(
      {
        ids: indicatorsIds,
        companyId,
        newWatchlist: newWatchlistStatus,
      },
      this.view.dom.$grid,
      this.view.dom.$sideMenu,
      'breach-path-side-menu',
      'BreachPathIssues'
    );
  }

  private async handleViewDetails(event: CustomEvent): Promise<void> {
    NavigationHelper.navigateTo(`/risk-management-breach-path/graph/${event.detail.data._id}`);
    this.closeSideMenu();
  }

  private async handleViewResources(event: CustomEvent): Promise<void> {
    NavigationHelper.navigateTo(`/risk-management-breach-path/resources/${event.detail.data._id}`);
    this.closeSideMenu();
  }

  private async handleOpenFeedbackFormEvent(event: CustomEvent): Promise<void> {
    // to be implemented
  }

  private async showSideMenu(breachPathIssue?: BreachPathIssueRowData) {
    if (this.view.dom.$sideMenu.isOpen) return;

    const component = new BreachPathSideMenuWebComponent();
    component.loading = true;
    this.view.dom.$sideMenu.setMenuContent(component);
    this.view.dom.$sideMenu.openSideMenu();

    const breachPathData = breachPathIssue ?? (await this.dataRepository.getBreachPathIssueDataById(this.view.breachPathIssueId))?.data;

    if (breachPathData) {
      component.breachPathData = breachPathData;
      component.loading = false;
      this.highlightGridRow();
    } else {
      this.closeSideMenu();
    }
  }

  private closeSideMenu(fireEvent = false) {
    this.view.dom.$sideMenu.closeSideMenu(fireEvent);
    this.view.dom.$sideMenu.setMenuContent(null);
    this.removeHighlightGridRow();
  }

  private highlightGridRow() {
    if (!this.view.dom.$grid.gridLoaded || !this.view.breachPathIssueId) {
      return;
    }

    const vaadinGrid = this.view.dom.$grid.shadowRoot.querySelector('vaadin-grid') as GridElement;

    if (vaadinGrid.items) {
      const itemIndex = vaadinGrid.items.findIndex(({ _id }: { _id: string }) => _id === this.view.breachPathIssueId);
      this.view.dom.$grid.controller.view.highlightGridRowByIndex(itemIndex);
    }
  }

  private removeHighlightGridRow() {
    if (this.view.dom.$grid.gridLoaded) {
      this.view.dom.$grid.controller.view.removeHighlightGridRows();
    }
  }

  private handleSideMenuClose() {
    NavigationHelper.navigateTo(`/risk-management-breach-path`);
  }
}
